import DeleteIcon from '@mui/icons-material/Delete';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import {
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Theme,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import * as Yup from 'yup';
import Axios from '../../common/axiosInterceptor';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../common/useMakePHPRequest';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';

import makeStyles from '@mui/styles/makeStyles';
import { FieldArray, Formik, FormikProps } from 'formik';
import { LabelledOutline } from '../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
  MaterialAutocomplete,
} from '../../common/formik-material-ui';
import { isUrl } from '../../common/validators';
import { IUserAtom, IUserModel, user } from '../../recoil/atoms/user';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../components/recorder/RecordNow';

import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import clsx from 'clsx';
import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';
import {
  trackModalUpdates,
  trackSaveRingGroup,
} from '../../common/userTracking';
import { Business } from '../../lib/kixie-entities/tables/Business';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import {
  createHoursString,
  Scheduler,
} from '../components/CadenceSettings/Scheduler';
import {
  DefaultAction,
  getDefaultActionPayload,
  getDefaultActionUrl,
} from '../components/dropdowns/DefaultAction';
import TabLabel from '../components/general/TabLabel';
import {
  getErrorTabIndex,
  IGetBusinessDevice,
  isErrorInTab,
} from '../components/modals/DevicesAndRoutingModal';
import { ModalWithStyles } from '../components/modals/ModalWithStyles';
import { WizardModal } from '../components/modals/WizardModal';

const tabNames = [
  {
    title: 'Settings',
    icon: SettingsApplicationsIcon,
  },
  {
    title: 'Ring Groups',
    icon: NotificationsActiveIcon,
  },
  {
    title: 'Ring Groups Hours',
    icon: QueryBuilderIcon,
  },
];
function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
const special = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
  'Eleventh',
  'Twelfth',
  'Thirteenth',
  'Fourteenth',
  'Fifteenth',
  'Sixteenth',
  'Seventeenth',
  'Eighteenth',
  'Nineteenth',
];
function stringifyNumber(n: number) {
  if (n < 20) {
    return special[n];
  }
}
const ringTypes = [
  {
    name: 'simultaneous',
  },
  {
    name: 'linear',
  },
  {
    special: 'Linear-RoundRobin',
    name: 'linear-roundrobin',
  },
];
// delete classes not being used
const useStyles = makeStyles((theme: Theme) => ({
  tabs: {},
  tabIcon: {
    marginRight: 15,
  },
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  customButton: {
    marginLeft: theme.spacing(1), // space between fields
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  delete: {
    padding: 0, // shrink delete icon on action button
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  fileUploadLabel: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: 'min-content',
  },
  addButton: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiButton-label': {
      padding: 2,
    },
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },

  audio: {
    '&:focus': {
      outline: 0,
    },
  },
  warningIcon: {
    color: 'orange',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  editHours: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  editHoursButton: {
    height: 'fit-content',
    alignSelf: 'center',
  },
  selectAgent: {
    marginBottom: theme.spacing(0.5),
    marginTop: '8px',
  },
}));

export interface IRingGroupSetting {
  closedAction: string;
  greeting: string;
  groupemail: string;
  groupname: string;
  hasGreeting: 'no' | 'yes';
  hasRingMusic: 'no' | 'yes';
  hasdefaultAction: 'no' | 'yes';
  hasvmGreeting: 'no' | 'yes';
  initialAttempt:
    | 'ignoreOwnership'
    | 'routeToOwnerOnly'
    | 'routeToOwnerThenProceed';
  postAction: string;
  ringGroupHours: string; // ISchedulableHours;
  ringMusic: string;
  tiers: [
    {
      grouporder: string;
      grouptype: 'simultaneous' | 'linear' | 'linear-roundrobin';
      members: [
        {
          deviceType: string;
          email: string;
          fname: string;
          lname: string;
          phoneType: string;
          ringorder: string; // number
          sipUserid: string; // number
          userid: string; // number
        }
      ];
      numberOfMembers: number;
      pressone: number; // number
      ringgroupid: string; // number
      timeout: string; // number
    }
  ];
  useBusinessHoursEnabled: '0' | '1';
  vmGreeting: string;
}

export function RingGroupSettings(props: {
  show: boolean;
  onHide: () => void;
  groupid?: string;
}) {
  const [tabIndex, setTabIndex] = useState(0);
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  const handleChange = (formik: FormikProps<any>) => async (
    _event: React.ChangeEvent<any>,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);
  };
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const userLoadable = useRecoilValueLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;

  const [submitStatus, setSubmitStatus] = useState(loadableEnum.EMPTY);

  // console.log(devices, 'buseinses devices ---');
  const innerRef = useRef<any>();
  // console.log(ref.current, 'inner reference to formik ----');
  // useRecoilValueLoadable(submitGroupRingGroup);
  const classes = useStyles();
  //   const [ringMusicFile, setRingMusicFile] = useState();
  // console.log(submitFormResult, 'result of submitting form');
  let { id: groupid } = useParams<{ id: string | undefined }>();
  if (!groupid && props.groupid) {
    groupid = props.groupid;
  }
  // const groupid = props.groupid ?? useParams<{ id: string | undefined }>();

  const [businessCached, _businessLoadable] = makeApiRequestCached<
    Business | undefined
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userContents.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  const ringGroupSettingsCached = useRef<IRingGroupSetting>();
  const ringGroupSettingsLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_GROUP_RING_GROUP_SETTINGS,
      payloadJsonString: JSON.stringify({
        groupid,
      }),
      waitForPayload: groupid === undefined, // do not call backend if no id
    })
  );
  ringGroupSettingsCached.current =
    ringGroupSettingsLoadable.contents?.data?.result ??
    ringGroupSettingsCached.current;
  const [
    businessUsersActiveLookup,
    _businessUsersActiveLoadable,
  ] = makeApiRequestCached<{ [key: string]: IUserModel }>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])
        ?.filter((user) => user.isactive === 1)
        .reduce((a, u) => ({ ...a, [u.userid]: u }), {}) ?? cached.current,
    {}
  );

  const sortedbusinessUsersActiveLookupKeys = Object.keys(
    businessUsersActiveLookup?.current
  )
    .concat('')
    .sort((a, b) => {
      const userA = businessUsersActiveLookup.current[a];
      const userB = businessUsersActiveLookup.current[b];
      let sorted;
      if (userA?.fname + userA?.lname > userB?.fname + userB?.lname) {
        sorted = 1;
      } else if (userA?.fname + userA?.lname < userB?.fname + userB?.lname) {
        sorted = -1;
      } else {
        sorted = 0;
      }
      return sorted;
    });

  const [
    businessDevicesCached,
    _businessDevicesLoadable,
  ] = makeApiRequestCached<IGetBusinessDevice[] | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DEVICES,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DEVICES,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const setRefetchToggleForRingGroupSettings = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_GROUP_RING_GROUP_SETTINGS)
  );

  // console.log(groupCardData, 'data');
  // console.log(settings, 'all the settings we need');
  const makeRequest = useMakePHPRequest({
    userAtom: userContents,
    setStatus: setSubmitStatus,
  });
  const onChangeFile = (type: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        (files?.length ?? 0) > 1
          ? `${files?.length} files`
          : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };

  const handleRecording = (type: string) => (files: Record<string, any>) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };

  const schedulableHoursCached = useRef();
  const handleSetSchedulableHours = (hours: any) => {
    schedulableHoursCached.current = hours;
  };

  const [showRingGroupHours, setShowRingGroupHours] = useState(false);
  const themeMode = useRecoilValue(themeModeAtom);

  const handleRingGroupHoursSubmit = (formik: FormikProps<any>) => (
    _event: React.MouseEventHandler<HTMLButtonElement>
  ) => {
    if (!createHoursString(schedulableHoursCached.current)) {
      enqueueSnackbarWithStyles({
        message: 'Please select a time range',
        alertProps: {
          severity: 'warning',
        },
      });
    } else {
      formik.setFieldValue(
        'submitRingGroupPayload.ringGroupHours',
        schedulableHoursCached.current
      );
      setShowRingGroupHours(false);
    }
  };

  const [uploadedFiles, setUploadedFiles] = useState<any>({});
  const [filepaths, setFilepaths] = useState({
    voicemail: isUrl(ringGroupSettingsCached.current?.vmGreeting)
      ? ringGroupSettingsCached.current?.vmGreeting ?? ''
      : '',
    greeting: isUrl(ringGroupSettingsCached.current?.greeting)
      ? ringGroupSettingsCached.current?.greeting ?? ''
      : '',
    ring: isUrl(ringGroupSettingsCached.current?.ringMusic)
      ? ringGroupSettingsCached.current?.ringMusic ?? ''
      : '',
  });
  const [filenames, setFilenames] = useState({
    voicemail: isUrl(ringGroupSettingsCached.current?.vmGreeting)
      ? ringGroupSettingsCached.current?.vmGreeting
          .split('/')
          .slice(-1)
          .pop() ?? ''
      : '', // filename from uri/path
    greeting: isUrl(ringGroupSettingsCached.current?.greeting)
      ? ringGroupSettingsCached.current?.greeting.split('/').slice(-1).pop() ??
        ''
      : '', // filename from uri/path
    ring: isUrl(ringGroupSettingsCached.current?.ringMusic)
      ? ringGroupSettingsCached.current?.greeting.split('/').slice(-1).pop() ??
        ''
      : '', // filename from uri/path
  });
  useEffect(() => {
    setFilenames({
      voicemail: isUrl(ringGroupSettingsCached.current?.vmGreeting)
        ? ringGroupSettingsCached.current?.vmGreeting
            .split('/')
            .slice(-1)
            .pop() ?? ''
        : '', // filename from uri/path
      greeting: isUrl(ringGroupSettingsCached.current?.greeting)
        ? ringGroupSettingsCached.current?.greeting
            .split('/')
            .slice(-1)
            .pop() ?? ''
        : '', // filename from uri/path
      ring: isUrl(ringGroupSettingsCached.current?.ringMusic)
        ? ringGroupSettingsCached.current?.ringMusic
            .split('/')
            .slice(-1)
            .pop() ?? ''
        : '', // filename from uri/path
    });
    setFilepaths({
      voicemail: isUrl(ringGroupSettingsCached.current?.vmGreeting)
        ? ringGroupSettingsCached.current?.vmGreeting ?? ''
        : '',
      greeting: isUrl(ringGroupSettingsCached.current?.greeting)
        ? ringGroupSettingsCached.current?.greeting ?? ''
        : '',
      ring: isUrl(ringGroupSettingsCached.current?.ringMusic)
        ? ringGroupSettingsCached.current?.ringMusic ?? ''
        : '',
    });
  }, [ringGroupSettingsLoadable]);

  //id of this object
  const { id } = useParams<{ id?: string }>();
  // console.log('Current ID: "', id, '"');

  const EditRingGroupSchema = useMemo(
    () =>
      Yup.object({
        submitRingGroupPayload: Yup.object({
          groupName: Yup.string().required('Required'),
          groupEmail: Yup.string()
            .commaSeparatedEmails('Must be a email')
            .required('Required'),
        }),
        ringGroupCards: Yup.array().of(
          Yup.object().shape({
            tier: Yup.object({
              timeout: Yup.number()
                .required('Required')
                .typeError('Must be a number'),
              members: Yup.array().of(
                Yup.object().shape({
                  userid: Yup.string().required('Required'),
                  sipUserId: Yup.string().required('Required'),
                })
              ),
            }),
          })
        ),
        submitRingGroupGreetingMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        submitGroupRingAudioFile: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        submitRingGroupVoicemailMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
      }),
    []
  );

  interface IRingGroupCard {
    tier: {
      groupOrder: string;
      groupType: 'simultaneous' | 'linear' | 'linear-roundrobin';
      numberOfMembers: string;
      pressOne: boolean;
      ringgroupid: string;
      timeout: string; // number
      members: [
        {
          deviceType: string;
          email: string;
          name: string;
          phoneType: string;
          ringOrder: number;
          sipUserId: string;
          userid: string;
        }
      ];
    };
  }

  // get all defaults based on queue
  const tabToFields = [
    [
      'submitRingGroupGreetingMp3File',
      'submitRingGroupVoicemailMp3File',
      'submitGroupRingAudioFile',
      'defaultActionPayload',
      'submitRingGroupPayload',
    ],
    ['ringGroupCards'],
    ['submitRingGroupPayload.ringGroupHours', 'closedActionPayload'],
  ];
  const ringGroupInitialValues = {
    submitRingGroupGreetingMp3File: {
      files: '',
      name: ringGroupSettingsLoadable?.contents?.data?.result?.greeting ?? '',
    },
    submitRingGroupVoicemailMp3File: {
      files: '',
      name: ringGroupSettingsLoadable?.contents?.data?.result?.vmGreeting ?? '',
    },
    submitGroupRingAudioFile: {
      files: '',
      name: ringGroupSettingsLoadable?.contents?.data?.result?.ringMusic ?? '',
    },
    defaultActionPayload: getDefaultActionPayload(
      ringGroupSettingsCached.current?.postAction,
      true,
      undefined,
      id
    ),
    closedActionPayload: getDefaultActionPayload(
      ringGroupSettingsCached.current?.closedAction,
      true,
      undefined,
      id
    ),
    submitRingGroupPayload: {
      groupName: ringGroupSettingsCached.current?.groupname ?? '',
      groupEmail: ringGroupSettingsCached.current?.groupemail ?? '',
      greetingType:
        // does he/she have a greeting
        ringGroupSettingsCached.current?.hasGreeting === 'yes'
          ? // if so does the greeting contain an mp3 url?
            ringGroupSettingsCached.current?.greeting.endsWith('.mp3')
            ? // if so set the greeting type to mp3
              'mp3'
            : // otherwise we assume it is a tts value
              'tts'
          : 'none',
      greeting: ringGroupSettingsCached.current?.greeting?.endsWith('.mp3')
        ? '' // do not show mp3 file name as greeting
        : ringGroupSettingsCached.current?.greeting, // show greeting
      voicemailType: ringGroupSettingsCached.current?.vmGreeting?.endsWith(
        '.mp3'
      )
        ? 'mp3'
        : 'tts',
      voicemailGreeting: ringGroupSettingsCached.current?.vmGreeting?.length
        ? ringGroupSettingsCached.current?.vmGreeting
        : 'Record your message at the tone, after you are done, press pound',
      ringAudioType:
        ringGroupSettingsCached.current?.hasRingMusic === 'yes'
          ? 'mp3'
          : 'none',
      ringAudio: ringGroupSettingsCached.current?.ringMusic,
      initialAttempt:
        ringGroupSettingsCached.current?.initialAttempt ?? 'ignoreOwnership',
      useBusinessHoursEnabled:
        ringGroupSettingsCached.current === undefined
          ? true // default to true
          : ringGroupSettingsCached.current?.useBusinessHoursEnabled == '1',
      ringGroupHours:
        JSON.parse(
          (ringGroupSettingsCached.current?.ringGroupHours || null) as string
        ) ?? businessCached.current?.businessDetails?.businessHours,
    },
    ringGroupCards: (ringGroupSettingsCached.current?.tiers?.map(
      (card, index) => ({
        tier: {
          groupOrder: card?.grouporder ?? '',
          groupType: card.grouptype ?? '',
          numberOfMembers: card.numberOfMembers ?? '',
          pressOne: card.pressone === 1,
          ringgroupid: card.ringgroupid ?? '',
          timeout: card.timeout ?? '5',
          members: card.members.map((memb, index) => ({
            deviceType: memb?.deviceType ?? '',
            email: memb.email ?? '',
            name: `${memb.fname} ${memb.lname}`,
            phoneType: memb?.phoneType ?? '',
            ringOrder: index + 1,
            sipUserId: +memb.sipUserid,
            userid: +memb.userid,
          })),
        },
      })
    ) ?? [
      {
        tier: {
          groupOrder: '',
          groupType: 'simultaneous',
          numberOfMembers: '',
          pressOne: false,
          ringgroupid: '',
          timeout: '25',
          members: [
            {
              deviceType: '',
              email: '',
              name: '',
              phoneType: '',
              ringOrder: 1,
              sipUserId: '',
              userid: '',
            },
          ],
        },
      },
    ]) as IRingGroupCard[],
  };
  function trackUpdates(values, ringGroupInitialValues, currentTabTitle) {
    trackModalUpdates(
      values,
      ringGroupInitialValues,
      'Ring Group Updated',
      currentTabTitle
    );
  }
  return (
    <Formik
      innerRef={innerRef}
      enableReinitialize={true}
      validationSchema={EditRingGroupSchema}
      initialValues={ringGroupInitialValues}
      onSubmit={async (values, actions) => {
        trackUpdates(values, ringGroupInitialValues, currentTabTitle);
        let newGroupId: string | undefined;
        if (groupid === undefined) {
          const { response, exception } = await makeRequest({
            methodName: methodNameEnum.SUBMIT_NEW_GROUP,
            payload: {
              groupname: values.submitRingGroupPayload.groupName,
              vmemail: values.submitRingGroupPayload.groupEmail,
            },
          });
          newGroupId = response?.data?.result?.groupid;
          const resMessage = response?.data;
          // Check to see if the Group Name is already used
          // 1062 = duplicate group name
          if (resMessage.code === 1062) {
            enqueueSnackbarWithStyles({
              message: 'Please select a Group Name that is not already in use',
              alertProps: { severity: 'error' },
            });
            return;
          } else if (exception) {
            enqueueSnackbarWithStyles({
              message: 'Failed to Create New Group: ' + exception.message,
              alertProps: { severity: 'error' },
            });
            return;
          }
        }
        // console.log(values.ringGroupCards, 'ring group cards');
        const { response: _response } = await makeRequest({
          methodName: methodNameEnum.SUBMIT_RING_GROUP_SETTINGS,
          payload: {
            groupid: groupid ?? newGroupId,
            groupRinggroup: JSON.stringify({
              general: {
                name: values.submitRingGroupPayload.groupName,
                email: values.submitRingGroupPayload.groupEmail,
                greetingType: values.submitRingGroupPayload.greetingType,
                greeting:
                  values.submitRingGroupPayload.greetingType === 'tts'
                    ? values.submitRingGroupPayload.greeting
                    : '',
                ringAudioType: values.submitRingGroupPayload.ringAudioType,
                ringAudio: '',
                voicemailType: values.submitRingGroupPayload.voicemailType,
                voicemail:
                  values.submitRingGroupPayload.voicemailType === 'tts'
                    ? values.submitRingGroupPayload.voicemailGreeting
                    : '',
                defaultAction: getDefaultActionUrl({
                  ...values.defaultActionPayload,
                  businessid: userContents?.businessid,
                  parentType: 'group',
                  voicemail: groupid ?? (newGroupId as string),
                }),
                closedAction: getDefaultActionUrl({
                  ...values.closedActionPayload,
                  businessid: userContents?.businessid,
                  parentType: 'group',
                  voicemail: groupid ?? (newGroupId as string),
                }),
                initialAttempt: values.submitRingGroupPayload.initialAttempt,
                useBusinessHoursEnabled:
                  values.submitRingGroupPayload.useBusinessHoursEnabled,
                ringGroupHours: JSON.stringify(
                  values.submitRingGroupPayload.ringGroupHours
                ),
              },
              tiers: values.ringGroupCards.map((card, index) => ({
                type: card.tier.groupType,
                groupOrder: index,
                pressOne: card.tier.pressOne ? 'on' : 'off',
                duration: card.tier.timeout,
                members: values.ringGroupCards[index].tier.members.map(
                  (memb, index) => ({
                    position: memb.ringOrder,
                    userid: memb.userid,
                    sipuserid: memb.sipUserId,
                  })
                ),
              })),
            }),
          },
        });
        const dupNameError = _response?.data?.code === 1062; // 1062 - duplicate key violation
        if (dupNameError) {
          enqueueSnackbarWithStyles({
            message: 'Please select a Group Name that is not already in use',
            alertProps: { severity: 'error' },
          });
          return;
        }
        if (
          uploadedFiles.greeting &&
          values.submitRingGroupPayload.greetingType === 'mp3'
        ) {
          // console.log('we found an mp3 GREETING FILE');
          const groupGreetingData = new FormData();
          groupGreetingData.append('call', 'submitGroupGreetingMp3File');
          groupGreetingData.append('groupid', String(groupid ?? newGroupId));
          groupGreetingData.append(
            'businessid',
            String(userContents.businessid)
          );
          groupGreetingData.append('apiKey', userContents.apikey);
          groupGreetingData.append('mp3', uploadedFiles?.greeting[0]);
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          const _mp3ResultGreeting = await Axios.post(url, groupGreetingData, {
            withCredentials: true,
          });
        }
        if (
          uploadedFiles.greeting &&
          values.submitRingGroupPayload.greetingType === 'record'
        ) {
          const formData = new FormData();
          formData.append('webm', uploadedFiles.greeting[0]);
          formData.append('businessid', String(userContents?.businessid));
          formData.append('apikey', userContents?.apikey);
          formData.append('userid', String(groupid ?? newGroupId)); // user id of agent being changed
          formData.append('id', String(groupid ?? newGroupId)); // user id of agent being changed
          formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
          formData.append('iid', String(0)); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
          formData.append('name', values.submitRingGroupPayload.groupName); // for vm drop name MUST be name of voicemail drop
          formData.append('type', 'group-greeting'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
          const _response3 = await Axios.post(
            `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
            formData
          );
        }
        if (
          uploadedFiles.voicemail &&
          values.submitRingGroupPayload.voicemailType === 'mp3'
        ) {
          // console.log('we found an mp3 VOICEMAIL FILE');
          const groupGreetingData = new FormData();
          groupGreetingData.append('call', 'submitGroupVoicemailMp3File');
          groupGreetingData.append('groupid', String(groupid ?? newGroupId));
          groupGreetingData.append(
            'businessid',
            String(userContents.businessid)
          );
          groupGreetingData.append('apikey', userContents.apikey);
          groupGreetingData.append('mp3', uploadedFiles?.voicemail[0]);
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          const _mp3ResultGreeting = await Axios.post(url, groupGreetingData, {
            withCredentials: true,
          });
          // console.log(
          //   _mp3ResultGreeting,
          //   'result of submitting mp333333 voicemail file'
          // );
        }
        if (
          uploadedFiles.voicemail &&
          values.submitRingGroupPayload.voicemailType === 'record'
        ) {
          const formData = new FormData();
          formData.append('webm', uploadedFiles.voicemail[0]);
          formData.append('businessid', String(userContents?.businessid));
          formData.append('apikey', userContents?.apikey);
          formData.append('userid', String(groupid ?? newGroupId)); // user id of agent being changed
          formData.append('id', String(groupid ?? newGroupId)); // user id of agent being changed
          formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
          formData.append('iid', String(0)); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
          formData.append('name', values.submitRingGroupPayload.groupName); // for vm drop name MUST be name of voicemail drop
          formData.append('type', 'group-voicemail'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
          const _response3 = await Axios.post(
            `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
            formData
          );
        }
        if (
          uploadedFiles.ring &&
          values.submitRingGroupPayload.ringAudioType === 'mp3'
        ) {
          // console.log('we found an mp3 RING AUDIO FILE');
          const groupGreetingData = new FormData();
          groupGreetingData.append('call', 'submitGroupRingAudioMp3File');
          groupGreetingData.append('groupid', String(groupid ?? newGroupId));
          groupGreetingData.append(
            'businessid',
            String(userContents?.businessid)
          );
          groupGreetingData.append('apikey', userContents.apikey);
          groupGreetingData.append('mp3', uploadedFiles?.ring[0]);
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          const _mp3ResultGreeting = await Axios.post(url, groupGreetingData, {
            withCredentials: true,
          });
          // console.log(
          //   _mp3ResultGreeting,
          //   'result of submitting mp333333 ring audio file'
          // );
        }
        setRefetchToggleForRingGroupSettings({});
        actions.setSubmitting(false);
        props.onHide();
        enqueueSnackbarWithStyles({
          message: 'Successfully Updated Ring Group Settings',
          alertProps: { severity: 'success' },
        });
      }}
    >
      {(formik) => (
        <>
          <TabContext value={String(tabIndex)}>
            <WizardModal
              title={tabNames[tabIndex].title}
              disableSubmit={!formik.isValid}
              navigation={
                <>
                  <Tab
                    label={`${
                      groupid === undefined ? 'Add' : 'Edit'
                    } Ring Group`}
                    disabled={true}
                    className={clsx(classes.tab)}
                  />
                  <TabList
                    onChange={handleChange(formik)}
                    aria-label="Edit Ring Group"
                    orientation="vertical"
                    variant="standard"
                    className={clsx(classes.tabs, {})}
                  >
                    {tabNames.map((tabItem, index) => (
                      <Tab
                        {...{ name: tabItem.title }}
                        key={index}
                        className={classes.tab}
                        label={
                          <TabLabel
                            index={index}
                            formik={formik}
                            tabToFields={tabToFields}
                            tabItem={tabItem}
                          />
                        }
                        value={String(index)}
                      />
                    ))}
                  </TabList>
                </>
              }
              dialogActions={
                <>
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="inherit"
                    disabled={submitStatus === loadableEnum.LOADING}
                    onClick={props.onHide}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={classes.customButton}
                    variant="contained"
                    color="primary"
                    disabled={
                      !formik.dirty || submitStatus === loadableEnum.LOADING
                    }
                    onClick={(event) => {
                      // trigger show errors if errors exists
                      formik.handleSubmit(
                        (event as any) as React.FormEvent<HTMLFormElement>
                      );

                      // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                      if (
                        !formik.isValid && // if form invalid
                        !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                      ) {
                        const errorTabIndex = getErrorTabIndex(
                          formik.errors,
                          tabToFields
                        );
                        /**
                         * switch to tab containing error
                         *
                         * since form validation is in flight and makes page rendering updates
                         * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                         * putting state changes in a separate tick will prevent coliding page rendering updates
                         *
                         * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                         */
                        setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                      }
                    }}
                  >
                    Save
                  </Button>
                </>
              }
              isLoading={
                submitStatus === loadableEnum.LOADING ||
                ringGroupSettingsLoadable.state === 'loading'
              }
              size="md"
              show={props.show}
              onHide={props.onHide}
              onSubmit={() => {
                trackSaveRingGroup();
                formik.handleSubmit();
              }}
            >
              <TabPanel value="0">
                <FormikTextField
                  name="submitRingGroupPayload.groupName"
                  className={classes.field}
                  formik={formik}
                  fullWidth
                  label="Group Name"
                  size="small"
                  required
                  variant="outlined"
                />
                <FormikTextField
                  name="submitRingGroupPayload.groupEmail"
                  className={classes.field}
                  formik={formik}
                  fullWidth
                  label="Send Voicemail to (comma separated emails)"
                  size="small"
                  required
                  variant="outlined"
                />
                <FormikSelect
                  name="submitRingGroupPayload.initialAttempt"
                  className={classes.field}
                  formik={formik}
                  fullWidth
                  //label="Initial Attempt Behavior"
                  size="small"
                  variant="outlined"
                  hidden={true}
                >
                  <MenuItem value="ignoreOwnership">Ignore Ownership</MenuItem>
                  <MenuItem value="routeToOwnerOnly">
                    Route To Owner Only
                  </MenuItem>
                  <MenuItem value="routeToOwnerThenProceed">
                    Route To Owner Then Proceed To Add to List
                  </MenuItem>
                </FormikSelect>
                <RecordNow
                  dropdownLabel="Group Greeting Type"
                  dropdownName="submitRingGroupPayload.greetingType"
                  dropdownType={
                    formik.values.submitRingGroupPayload?.greetingType as any
                  }
                  outlineLabel="Group Greeting Type"
                  ttsLabel="Group Greeting"
                  ttsName="submitRingGroupPayload.greeting"
                  filenameName="submitRingGroupGreetingMp3File.files"
                  filesizeName="submitRingGroupGreetingMp3File.name"
                  formik={formik}
                  filename={filenames.greeting}
                  filepath={filepaths.greeting}
                  onChangeFile={onChangeFile('greeting')}
                  onRecording={handleRecording('greeting')}
                >
                  <MenuItem value="none">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="tts">Text-to-Speech</MenuItem>
                  <MenuItem value="mp3">MP3</MenuItem>
                  <MenuItem value="record">Record Now</MenuItem>
                </RecordNow>
                <RecordNow
                  dropdownLabel="Group Ring Audio Type"
                  dropdownName="submitRingGroupPayload.ringAudioType"
                  dropdownType={
                    formik.values.submitRingGroupPayload?.ringAudioType as any
                  }
                  outlineLabel="Group Ring Audio"
                  filenameName="submitGroupRingAudioFile.files"
                  filesizeName="submitGroupRingAudioFile.name"
                  formik={formik}
                  filename={filenames.ring}
                  filepath={filepaths.ring}
                  onChangeFile={onChangeFile('ring')}
                  onRecording={handleRecording('ring')}
                >
                  <MenuItem value="none">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="mp3">MP3</MenuItem>
                </RecordNow>
                <RecordNow
                  dropdownLabel="Group Voicemail Type"
                  dropdownName="submitRingGroupPayload.voicemailType"
                  dropdownType={
                    formik.values.submitRingGroupPayload?.voicemailType as any
                  }
                  outlineLabel="Group Voicemail Type"
                  ttsLabel="Group Voicemail"
                  ttsName="submitRingGroupPayload.voicemailGreeting"
                  filenameName="submitRingGroupVoicemailMp3File.files"
                  filesizeName="submitRingGroupVoicemailMp3File.name"
                  formik={formik}
                  filename={filenames.voicemail}
                  filepath={filepaths.voicemail}
                  onChangeFile={onChangeFile('voicemail')}
                  onRecording={handleRecording('voicemail')}
                >
                  <MenuItem value="tts">Text-to-Speech</MenuItem>
                  <MenuItem value="mp3">MP3</MenuItem>
                  <MenuItem value="record">Record Now</MenuItem>
                </RecordNow>
                <DefaultAction
                  defaultAction={getDefaultActionUrl({
                    type: formik.values.defaultActionPayload?.type,
                    parentType: 'group',
                    businessid: userContents.businessid,
                    user: formik.values.defaultActionPayload?.user,
                    group: formik.values.defaultActionPayload?.group,
                    ivr: formik.values.defaultActionPayload?.ivr,
                    queue: formik.values.defaultActionPayload?.queue,
                    voicemail: '',
                  })}
                  actionType={formik.values.defaultActionPayload?.type}
                  typeName="defaultActionPayload.type"
                  ivrName="defaultActionPayload.ivr"
                  groupName="defaultActionPayload.group"
                  userName="defaultActionPayload.user"
                  queueName="defaultActionPayload.queue"
                  formik={formik}
                  disableFormUpdate={true}
                  id={id}
                />
              </TabPanel>
              {/* <Button variant="primary" onClick={formik.handleSubmit}>
                      Save All Changes
                    </Button> */}
              <TabPanel value="1">
                <FieldArray
                  name="ringGroupCards"
                  render={(arrayHelpers) =>
                    formik?.values?.ringGroupCards?.map((card, index) => (
                      <div key={index}>
                        <LabelledOutline
                          label={`${stringifyNumber(index)} Group`}
                          backgroundColor={
                            themeMode === themeModeEnum.DARK_MODE
                              ? '#121c21'
                              : '#fff'
                          }
                        >
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <FormikSelect
                                  name={`ringGroupCards.${index}.tier.groupType`}
                                  className={classes.field}
                                  formik={formik}
                                  fullWidth
                                  label="Ring Type"
                                  size="small"
                                  variant="outlined"
                                >
                                  {ringTypes.map((ring, index2) => (
                                    <MenuItem key={index2} value={ring.name}>
                                      {ring.special
                                        ? ring.special
                                        : capitalize(ring.name)}
                                    </MenuItem>
                                  ))}
                                </FormikSelect>
                              </div>
                              <div className="col">
                                <FormikTextField
                                  name={`ringGroupCards.${index}.tier.timeout`}
                                  className={classes.field}
                                  formik={formik}
                                  fullWidth
                                  helperText="(5 seconds = 1 ring)"
                                  label="Seconds To Ring"
                                  size="small"
                                  type="number"
                                  required
                                  variant="outlined"
                                />
                              </div>
                              <div className="col">
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name={`ringGroupCards.${index}.tier.pressOne`}
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                    />
                                  }
                                  label="Press One To Accept"
                                />
                              </div>
                              <div className="w-100">
                                {/* <header className={classes.header}>
                                  Devices To Ring
                                </header> */}
                              </div>
                              {/* <LabelledOutline label="Devices To Ring"> */}
                              <div
                                className="col-lg-2"
                                style={{ marginTop: '6px' }}
                              >
                                {formik.values.ringGroupCards[
                                  index
                                ].tier.members.map((member, index2: number) => (
                                  <FormikTextField
                                    style={{
                                      marginTop: '6px',
                                      marginBottom: '7px',
                                    }}
                                    key={index2}
                                    name={`ringGroupCards.${index}.tier.members.${index2}.ringOrder`}
                                    formik={formik}
                                    fullWidth
                                    label="Position"
                                    size="small"
                                    disabled={true}
                                    type="number"
                                    required
                                    variant="outlined"
                                  />
                                ))}
                              </div>
                              <div className="col-lg-4">
                                {formik.values.ringGroupCards[
                                  index
                                ].tier.members.map((member, index2) => {
                                  // Deduplicate and sort options once per MaterialAutocomplete
                                  const uniqueOptions = Array.from(
                                    new Set(
                                      sortedbusinessUsersActiveLookupKeys.map(
                                        (key) =>
                                          String(key).trim().toLowerCase()
                                      )
                                    )
                                  );
                                  return (
                                    <MaterialAutocomplete
                                      key={`member-${index}-${index2}`} // More stable key using both indices
                                      // className={classes.selectAgent}
                                      name={`ringGroupCards.${index}.tier.members.${index2}.userid`}
                                      onClose={(_event, _reason) => {
                                        // Clear devices if selecting a new agent
                                        formik.setFieldValue(
                                          `ringGroupCards.${index}.tier.members.${index2}.sipUserId`,
                                          ''
                                        );
                                      }}
                                      formik={formik}
                                      type="text"
                                      label="Select Agent"
                                      size="small"
                                      variant="outlined"
                                      options={uniqueOptions} // Use deduplicated and sorted options
                                      getOptionLabel={(option) => {
                                        const user =
                                          businessUsersActiveLookup.current[
                                            option
                                          ];
                                        return user
                                          ? `${user.fname} ${user.lname} (${option})`
                                          : '';
                                      }}
                                      isOptionEqualToValue={(option, value) =>
                                        option === value
                                      }
                                      required
                                    />
                                  );
                                })}
                              </div>
                              <div className="col-lg-4">
                                {formik.values.ringGroupCards[
                                  index
                                ].tier.members.map(
                                  (member, index2) =>
                                    businessDevicesCached.current && (
                                      <FormikSelect
                                        key={index2}
                                        name={`ringGroupCards.${index}.tier.members.${index2}.sipUserId`}
                                        className={classes.field}
                                        formik={formik}
                                        fullWidth
                                        required
                                        label="Select Device"
                                        size="small"
                                        variant="outlined"
                                      >
                                        {businessDevicesCached.current
                                          ?.filter(
                                            (device) =>
                                              device.userId ===
                                              +formik.values.ringGroupCards[
                                                index
                                              ].tier.members[index2].userid
                                          )
                                          .map((d, index3) => (
                                            <MenuItem
                                              key={index3}
                                              value={d.sipUserId}
                                            >
                                              {d.deviceType.includes('webrtc')
                                                ? 'PowerCall'
                                                : d.deviceType}
                                            </MenuItem>
                                          ))}
                                      </FormikSelect>
                                    )
                                )}
                              </div>
                              <div
                                // style={{ marginTop: '1vh' }}
                                className="col-lg-1"
                              >
                                {formik.values.ringGroupCards[
                                  index
                                ].tier.members.map((memb, index3) => (
                                  <Tooltip key={index3} title="add">
                                    <>
                                      <IconButton
                                        style={{ marginTop: '8px' }}
                                        aria-label="add"
                                        color="primary"
                                        onClick={() => {
                                          formik.setFieldValue(
                                            `ringGroupCards.${index}.tier.members`,
                                            [
                                              ...formik.values.ringGroupCards[
                                                index
                                              ].tier.members.slice(
                                                0,
                                                index3 + 1
                                              ),
                                              {
                                                deviceType: '',
                                                email: '',
                                                name: '',
                                                phoneType: '',
                                                ringOrder: index3 + 2,
                                                sipUserId: '',
                                                userid: '',
                                              },
                                              ...formik.values.ringGroupCards[
                                                index
                                              ].tier.members
                                                .slice(index3 + 1)
                                                .map((member, i: number) => ({
                                                  ...member,
                                                  ringOrder:
                                                    member.ringOrder + 1,
                                                })),
                                            ]
                                          );
                                        }}
                                        size="large"
                                      >
                                        <PlaylistAddIcon />
                                      </IconButton>
                                    </>
                                  </Tooltip>
                                ))}
                              </div>
                              <div className="col-lg-1">
                                {formik.values.ringGroupCards[
                                  index
                                ].tier.members.map((memb, index4) => (
                                  <Tooltip key={index4} title="remove">
                                    <>
                                      <IconButton
                                        style={{ marginTop: '8px' }}
                                        aria-label="cancel"
                                        onClick={() => {
                                          formik.setFieldValue(
                                            `ringGroupCards.${index}.tier.members`,
                                            [
                                              ...formik.values.ringGroupCards[
                                                index
                                              ].tier.members.slice(0, index4),
                                              ...formik.values.ringGroupCards[
                                                index
                                              ].tier.members
                                                .slice(index4 + 1)
                                                .map((member, i: number) => ({
                                                  ...member,
                                                  ringOrder:
                                                    member.ringOrder - 1,
                                                })),
                                            ]
                                          );
                                        }}
                                        disabled={
                                          formik.values.ringGroupCards[index]
                                            .tier.members.length <= 1
                                        }
                                        className={classes.deleteIconButton}
                                        size="large"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </>
                                  </Tooltip>
                                ))}
                              </div>
                              {/* </LabelledOutline> */}
                            </div>
                          </div>
                        </LabelledOutline>
                        <br />
                      </div>
                    )) ?? <></>
                  }
                />
                <Button
                  style={{ marginRight: '10px' }}
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    formik.setFieldValue('ringGroupCards', [
                      ...(formik?.values?.ringGroupCards ?? []),
                      {
                        tier: {
                          groupOrder: '',
                          groupType: '',
                          numberOfMembers: '1',
                          pressOne: false,
                          ringgroupid: '',
                          timeout: '',
                          members: [
                            {
                              deviceType: '',
                              email: '',
                              name: ' ',
                              phoneType: '',
                              ringOrder: 1,
                              sipUserId: '',
                              userid: '',
                            },
                          ],
                        },
                      },
                    ]);
                  }}
                >
                  Add Group
                </Button>
                <Button
                  style={{ marginRight: '10px' }}
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    formik.setFieldValue('ringGroupCards', [
                      ...(formik?.values?.ringGroupCards ?? []),
                      formik?.values?.ringGroupCards[
                        formik?.values?.ringGroupCards.length - 1
                      ],
                    ]);
                  }}
                >
                  Copy Group
                </Button>
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  disabled={formik?.values?.ringGroupCards?.length <= 1}
                  onClick={() => {
                    formik.setFieldValue(
                      'ringGroupCards',
                      formik?.values?.ringGroupCards?.slice(0, -1)
                    );
                  }}
                >
                  Remove Group
                </Button>
              </TabPanel>
              <TabPanel value="2">
                <>
                  <LabelledOutline
                    label="Ring Group Hours"
                    backgroundColor={
                      themeMode === themeModeEnum.DARK_MODE ? '#121c21' : '#fff'
                    }
                  >
                    <div className="row">
                      <div className="col-lg-6">
                        Actions will be routed:
                        {createHoursString(
                          formik.values.submitRingGroupPayload
                            .useBusinessHoursEnabled
                            ? businessCached.current?.businessDetails
                                ?.businessHours
                            : formik.values.submitRingGroupPayload
                                .ringGroupHours ||
                                businessCached.current?.businessDetails
                                  ?.businessHours
                        )
                          .split(',')
                          .map((string, index) => (
                            <div key={index}>
                              {string} <br />
                            </div>
                          ))}
                      </div>
                      <div className={clsx('col-lg-6', classes.editHours)}>
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="submitRingGroupPayload.useBusinessHoursEnabled"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Use Business Hours"
                        />
                        <Button
                          className={classes.editHoursButton}
                          variant="contained"
                          color="primary"
                          onClick={() => setShowRingGroupHours(true)}
                          disabled={
                            formik.values.submitRingGroupPayload
                              .useBusinessHoursEnabled
                          }
                        >
                          Edit Hours
                        </Button>
                      </div>
                    </div>
                  </LabelledOutline>
                  <DefaultAction
                    defaultAction={getDefaultActionUrl({
                      type: formik.values.closedActionPayload?.type,
                      parentType: 'group',
                      businessid: userContents.businessid,
                      user: formik.values.closedActionPayload?.user,
                      group: formik.values.closedActionPayload?.group,
                      ivr: formik.values.closedActionPayload?.ivr,
                      queue: formik.values.closedActionPayload?.queue,
                      voicemail: '',
                    })}
                    label="Outside Ring Group Hours Action"
                    labelForBusiness="Outside Business Hours Action"
                    actionType={formik.values.closedActionPayload?.type}
                    typeName="closedActionPayload.type"
                    ivrName="closedActionPayload.ivr"
                    groupName="closedActionPayload.group"
                    userName="closedActionPayload.user"
                    queueName="closedActionPayload.queue"
                    formik={formik}
                    disableFormUpdate={true}
                    id={id}
                  />
                  <ModalWithStyles
                    onSubmit={handleRingGroupHoursSubmit(formik)}
                    title={'Ring Group Hours'}
                    show={showRingGroupHours}
                    onHide={() => {
                      setShowRingGroupHours(false);
                    }}
                    isSubmitting={formik.isSubmitting}
                    size="lg"
                  >
                    <Scheduler
                      //if showBusinessHours is true we set cadence hours , otherwise set the target hours
                      schedulableHours={
                        formik.values.submitRingGroupPayload
                          .useBusinessHoursEnabled
                          ? businessCached.current?.businessDetails
                              ?.businessHours
                          : formik.values.submitRingGroupPayload
                              .ringGroupHours ||
                            businessCached.current?.businessDetails
                              ?.businessHours
                      }
                      setSchedulableHours={handleSetSchedulableHours}
                    />
                  </ModalWithStyles>
                </>
              </TabPanel>

              {/* </ModalWithStyles> */}
            </WizardModal>
          </TabContext>
        </>
      )}
    </Formik>
  );
}
